"use client";

import { useState } from "react";

interface LeadCaptureFormProps {
  agentName: string;
  industryName: string;
  cityName: string;
}

export default function LeadCaptureForm({
  agentName,
  industryName,
  cityName,
}: LeadCaptureFormProps) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          phone: phone || undefined,
          company_name: companyName || undefined,
          source: "programmatic-seo",
          privacy_consent: true,
          metadata: {
            agent: agentName,
            industry: industryName,
            city: cityName,
            page: `solutions/${agentName}/${industryName}/${cityName}`,
            captured_at: new Date().toISOString(),
          },
        }),
      });

      if (!res.ok) {
        throw new Error("Submission failed");
      }

      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
      // Fallback: redirect to signup after 3s if form keeps failing
      setTimeout(() => {
        if (!submitted) {
          window.location.href = `/signup?ref=seo&agent=${encodeURIComponent(agentName)}`;
        }
      }, 3000);
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
          <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-bold text-white">Your Free AI Audit Is On Its Way!</h3>
        <p className="text-gray-300">
          We&apos;ll email your personalised audit within 3 minutes. It shows exactly how our {agentName} can help your {industryName.toLowerCase()} business in {cityName}.
        </p>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Limited territories available in {cityName} — you&apos;re ahead of the queue.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your work email"
            required
            disabled={loading}
            className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-5 py-3.5 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none disabled:opacity-50 sm:flex-1"
          />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (optional)"
            disabled={loading}
            className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-5 py-3.5 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none disabled:opacity-50 sm:w-40"
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Company name (optional)"
            disabled={loading}
            className="w-full rounded-lg border border-gray-700 bg-gray-900/50 px-5 py-3.5 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none disabled:opacity-50 sm:flex-1"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full whitespace-nowrap rounded-lg bg-gradient-to-r from-[#4361ee] to-[#7c3aed] px-8 py-3.5 font-semibold text-white transition-all hover:shadow-lg hover:shadow-purple-500/25 disabled:opacity-70 sm:w-auto"
          >
            {loading ? "Setting up..." : "Get Your Free AI Audit Now"}
          </button>
        </div>
      </div>
      {error && (
        <p className="text-center text-sm text-red-400">{error}</p>
      )}
      <p className="text-center text-[10px] text-gray-500 dark:text-gray-400">
        By submitting, you agree to our{" "}
        <a href="/privacy" className="underline hover:text-gray-300">Privacy Policy</a>.
        We will not share your data with third parties.
      </p>
    </form>
  );
}
